/**
 * A ilustração do login: o mar ao fundo, a faixa de espuma, a areia com
 * grão e uma quadra de vôlei de praia com rede e bola.
 *
 * Toda cor vem de `styles/tokens.css` (paleta Holst e areia) — a ilustração
 * não é uma imagem, é desenho, para acompanhar a marca sem baixar arquivo
 * nenhum e para nunca ficar borrada.
 *
 * O painel onde ela mora é sempre teal escuro, nos dois temas; por isso as
 * cores aqui são as CRUAS da paleta, não as semânticas que mudam com o tema.
 */
export function IlustracaoDePraia() {
  const traco = (cor: string, largura = 2) => ({ stroke: cor, strokeWidth: largura, fill: 'none' });

  return (
    <svg
      viewBox="0 0 640 360"
      role="img"
      aria-label="Ilustração: quadra de vôlei de praia na areia, com o mar ao fundo"
    >
      <defs>
        {/*
          O grão da areia: ruído fractal pintado na cor da sombra de areia
          (`--areia-sombra`, 92 · 74 · 36 em 0–255, aqui em 0–1). Um filtro
          SVG não lê variável CSS, então a cor entra como número.
        */}
        <filter id="grao-de-areia" x="0" y="0" width="100%" height="100%">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.9"
            numOctaves="2"
            stitchTiles="stitch"
            result="ruido"
          />
          <feColorMatrix
            in="ruido"
            type="matrix"
            values="0 0 0 0 0.36  0 0 0 0 0.29  0 0 0 0 0.14  0.9 0 0 0 -0.36"
            result="pontos"
          />
          <feComposite in="pontos" in2="SourceGraphic" operator="in" />
        </filter>
      </defs>

      {/* O mar: três linhas de onda, da mais funda à mais rasa. */}
      <path
        d="M0 52 C 60 46 120 58 180 52 S 300 44 360 52 S 500 60 640 50"
        style={traco('var(--holst-900)')}
      />
      <path
        d="M0 78 C 70 70 140 86 210 78 S 350 68 420 78 S 560 88 640 76"
        style={traco('var(--holst-700)')}
      />
      <path
        d="M0 100 C 70 92 140 108 210 100 S 350 90 420 100 S 560 110 640 98"
        style={traco('var(--holst-600)')}
      />

      {/* A areia molhada, a espuma e a areia seca. */}
      <path
        d="M0 124 C 70 114 130 134 200 124 S 330 112 400 124 S 540 136 640 120 L640 360 L0 360 Z"
        style={{ fill: 'var(--areia-molhada)' }}
      />
      <path
        d="M0 118 C 70 108 130 128 200 118 S 330 106 400 118 S 540 130 640 114"
        style={{ ...traco('var(--holst-50)', 3), strokeLinecap: 'round' }}
      />
      <path
        d="M0 150 C 110 138 210 162 330 150 S 520 138 640 152 L640 360 L0 360 Z"
        style={{ fill: 'var(--areia-seca)' }}
      />
      <path
        d="M0 124 C 70 114 130 134 200 124 S 330 112 400 124 S 540 136 640 120 L640 360 L0 360 Z"
        filter="url(#grao-de-areia)"
      />

      {/* A quadra, em perspectiva: as linhas de fundo e a do meio. */}
      <polygon
        points="220,192 420,192 550,346 90,346"
        style={{ ...traco('var(--areia-branca)', 3), strokeLinejoin: 'round' }}
      />
      <line
        x1="165"
        y1="256"
        x2="475"
        y2="256"
        style={{ stroke: 'var(--areia-branca)', strokeWidth: 3 }}
      />

      {/* A rede e os postes. */}
      <ellipse
        cx="152"
        cy="258"
        rx="10"
        ry="3"
        style={{ fill: 'var(--areia-sombra)', opacity: 0.18 }}
      />
      <ellipse
        cx="488"
        cy="258"
        rx="10"
        ry="3"
        style={{ fill: 'var(--areia-sombra)', opacity: 0.18 }}
      />
      <rect
        x="150"
        y="212"
        width="340"
        height="28"
        style={{ fill: 'var(--holst-950)', opacity: 0.28 }}
      />
      <line
        x1="150"
        y1="222"
        x2="490"
        y2="222"
        style={{ stroke: 'var(--holst-950)', strokeWidth: 1, opacity: 0.5 }}
      />
      <line
        x1="150"
        y1="231"
        x2="490"
        y2="231"
        style={{ stroke: 'var(--holst-950)', strokeWidth: 1, opacity: 0.5 }}
      />
      <line
        x1="150"
        y1="210"
        x2="490"
        y2="210"
        style={{ stroke: 'var(--areia-branca)', strokeWidth: 4 }}
      />
      <line
        x1="150"
        y1="240"
        x2="490"
        y2="240"
        style={{ stroke: 'var(--areia-branca)', strokeWidth: 2 }}
      />
      <line
        x1="150"
        y1="200"
        x2="150"
        y2="257"
        style={{ stroke: 'var(--holst-950)', strokeWidth: 4, strokeLinecap: 'round' }}
      />
      <line
        x1="490"
        y1="200"
        x2="490"
        y2="257"
        style={{ stroke: 'var(--holst-950)', strokeWidth: 4, strokeLinecap: 'round' }}
      />

      {/* A bola, no ar, com a sombra dela na areia. */}
      <ellipse
        cx="404"
        cy="306"
        rx="13"
        ry="3.5"
        style={{ fill: 'var(--areia-sombra)', opacity: 0.16 }}
      />
      <circle cx="396" cy="164" r="11" style={{ fill: 'var(--areia-branca)' }} />
      <path
        d="M386 160 Q 396 170 407 160 M396 153 Q 392 164 396 175"
        style={{ ...traco('var(--holst-600)', 1.6), strokeLinecap: 'round' }}
      />
    </svg>
  );
}
